import {useCallback, useRef, useState} from 'react';
import {copyToClipboard} from '@Utils/clipboard.util';
import {noop} from '@Utils/general.util';

const FEEDBACK_TIMEOUT = 1500;

export interface CopyToClipboardWithFeedbackData {
  onCopy: VoidFunction;
  doShowFeedback: boolean;
}

export interface CopyToClipboardWithFeedbackOptions {
  onFeedbackShowComplete?: VoidFunction;
  feedbackTimeout?: number;
  /** Called once the clipboard has taken the text, before the feedback shows. */
  onCopied?: VoidFunction;
  /** Called when the browser refuses the clipboard write; no feedback shows. */
  onCopyFailed?: VoidFunction;
}

export const useCopyToClipboardWithFeedback = (
  textToCopy: string,
  {onFeedbackShowComplete = noop, feedbackTimeout = FEEDBACK_TIMEOUT, onCopied = noop, onCopyFailed = noop}: CopyToClipboardWithFeedbackOptions = {}
): CopyToClipboardWithFeedbackData => {
  const [doShowFeedback, setDoShowFeedback] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const onCopySuccess = (): void => {
    onCopied();
    clearTimeout(timeoutRef.current);
    setDoShowFeedback(true);
    timeoutRef.current = setTimeout(() => {
      setDoShowFeedback(false);
      onFeedbackShowComplete();
    }, feedbackTimeout);
  };

  const onCopy = useCallback((): void => {
    copyToClipboard(textToCopy).then(onCopySuccess, onCopyFailed).catch(noop);
  }, [textToCopy, onCopied, onCopyFailed]);

  return {onCopy, doShowFeedback};
};
